import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Tooltip, Typography } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import {
	ROW_KIND,
	type ShiftIncentiveEventSnapshotRow,
} from "../../models/ShiftIncentiveEvent";

const UNSCHEDULED_EVENT_TOOLTIP =
	"Event was not scheduled before the shift started.";
const NO_MATCHING_EVENT_TOOLTIP =
	"No production event for this section during the shift.";
const SCHEDULED_COLOR = "#5eaf60";
const NOT_SCHEDULED_COLOR = "#9e9e9e";
const SCHEDULE_SORT_RANK = {
	SCHEDULED: 0,
	NOT_SCHEDULED: 1,
	NOT_MATCHED: 2,
} as const;

const isScheduledEvent = (
	row: ShiftIncentiveEventSnapshotRow,
	isFutureShift: boolean,
) =>
	row.RowKind === ROW_KIND.EVENT &&
	(isFutureShift || row.ScheduledHours != null);

const getScheduleSortRank = (
	row: ShiftIncentiveEventSnapshotRow,
	isFutureShift: boolean,
) => {
	if (row.RowKind === ROW_KIND.RECONCILIATION) {
		return SCHEDULE_SORT_RANK.NOT_MATCHED;
	}

	return isScheduledEvent(row, isFutureShift)
		? SCHEDULE_SORT_RANK.SCHEDULED
		: SCHEDULE_SORT_RANK.NOT_SCHEDULED;
};

const ScheduledTag = ({
	row,
	isFutureShift,
}: {
	row: ShiftIncentiveEventSnapshotRow;
	isFutureShift: boolean;
}) => {
	const isScheduled = isScheduledEvent(row, isFutureShift);
	const tooltip =
		row.RowKind === ROW_KIND.RECONCILIATION
			? NO_MATCHING_EVENT_TOOLTIP
			: UNSCHEDULED_EVENT_TOOLTIP;

	const tag = (
		<Box
			component="span"
			sx={{
				width: 26,
				height: 26,
				borderRadius: "4px",
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: isScheduled ? SCHEDULED_COLOR : NOT_SCHEDULED_COLOR,
				color: "common.white",
				fontWeight: 700,
			}}
		>
			{isScheduled ? "Y" : "N"}
		</Box>
	);

	return isScheduled ? (
		tag
	) : (
		<Tooltip title={tooltip} arrow>
			{tag}
		</Tooltip>
	);
};

type Props = {
	rows: ShiftIncentiveEventSnapshotRow[];
	isFutureShift: boolean;
	title?: string;
};

export const ShiftIncentiveEventsPanel = ({
	rows,
	isFutureShift,
	title,
}: Props) => {
	const sortedRows = useMemo(
		() =>
			rows.toSorted(
				(a, b) =>
					getScheduleSortRank(a, isFutureShift) -
					getScheduleSortRank(b, isFutureShift),
			),
		[rows, isFutureShift],
	);

	const columnDefs = useMemo(
		(): ColDef<ShiftIncentiveEventSnapshotRow>[] => [
			{
				headerName: "Event",
				flex: 1.2,
				minWidth: 150,
				valueGetter: (params) => {
					const row = params.data;
					if (!row) return "—";
					if (row.RowKind === ROW_KIND.RECONCILIATION) {
						return "Not matched";
					}
					if (row.EventId == null) return "—";

					return `Event #${row.EventId}`;
				},
			},
			{
				headerName: "Section",
				flex: 1,
				minWidth: 140,
				valueGetter: (params) => params.data?.SectionName?.trim() || "—",
			},
			{
				headerName: "Scheduled",
				width: 100,
				minWidth: 100,
				cellRenderer: (
					params: ICellRendererParams<ShiftIncentiveEventSnapshotRow>,
				) =>
					params.data ? (
						<ScheduledTag row={params.data} isFutureShift={isFutureShift} />
					) : null,
			},
			{
				headerName: "Time",
				flex: 1,
				minWidth: 130,
				valueGetter: (params) => {
					const row = params.data;
					if (!row?.OverlapStart || !row.OverlapEnd) return "—";

					const eventStart = dxtToLocalServerTime(row.OverlapStart, "HH:mm");
					const eventEnd = dxtToLocalServerTime(row.OverlapEnd, "HH:mm");

					return `${eventStart} - ${eventEnd}`;
				},
			},
			{
				headerName: "Target TPH",
				field: "TargetTPH",
				flex: 1,
				minWidth: 100,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(1) : "—",
			},
			{
				headerName: "Target TPH Pct.",
				field: "TargetTPHPct",
				flex: 1,
				minWidth: 120,
				valueFormatter: (params) =>
					params.value != null ? `${Number(params.value).toFixed(1)}%` : "—",
			},
			{
				headerName: "Effective TPH",
				field: "EffectiveTPH",
				flex: 1,
				minWidth: 120,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(1) : "—",
			},
			{
				headerName: "Sch. Hours",
				field: "ScheduledHours",
				flex: 1,
				minWidth: 80,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(2) : "—",
			},
			{
				headerName: "Actual Hours",
				field: "ActualHours",
				flex: 1,
				minWidth: 100,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(2) : "—",
			},
			{
				headerName: "Target Tons",
				field: "TargetTons",
				flex: 1,
				minWidth: 100,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(0) : "—",
			},
			{
				headerName: "Finished Tons",
				field: "FinishedTons",
				flex: 1,
				minWidth: 110,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(0) : "—",
			},
		],
		[isFutureShift],
	);

	const defaultColDef = useMemo(
		(): ColDef<ShiftIncentiveEventSnapshotRow> => ({
			filter: false,
			sortable: false,
			resizable: true,
			editable: false,
			cellStyle: { textAlign: "center" },
			suppressHeaderMenuButton: true,
		}),
		[],
	);

	return (
		<Box
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper",
				overflow: "hidden",
			}}
		>
			{title && (
				<Box
					sx={{
						px: 1.5,
						py: 1,
						borderBottom: "1px solid",
						borderColor: "divider",
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 600 }}>
						{title}
					</Typography>
				</Box>
			)}
			<Box
				sx={{
					overflow: "hidden",
					"& .ag-header-cell-label": {
						justifyContent: "center",
					},
					"& .ag-layout-auto-height .ag-center-cols-viewport": {
						minHeight: "unset !important",
					},
				}}
			>
				<AgGridReact<ShiftIncentiveEventSnapshotRow>
					rowData={sortedRows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					rowHeight={34}
					headerHeight={34}
					domLayout="autoHeight"
					suppressCellFocus
					suppressContextMenu
					getRowId={(params) =>
						params.data.RowKind === ROW_KIND.EVENT
							? `${params.data.ShiftId}:event:${params.data.EventId}`
							: `${params.data.ShiftId}:reconciliation:${params.data.SectionId}`
					}
				/>
			</Box>
		</Box>
	);
};
